import posthog from "posthog-js";
import { trackMetaEvent } from "./metaPixel";
import { landedFromAd } from "utils/landingSource";
import { captureLandingFbclid } from "./metaAttribution";

// Same PostHog project (US Cloud, project 305562) that the One-Now-Frontend
// admin "monthly-review" dashboard reads back via HogQL — so customer-site
// traffic shows up per-tenant there. The phc_… key is a publishable client key.
const POSTHOG_KEY =
  process.env.REACT_APP_POSTHOG_KEY ||
  "phc_g5RzAulwRdukonaaOvc9690wQBbCLCWeujVaIJtYtM2";
const POSTHOG_HOST =
  process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

let initialized = false;

/**
 * The tracing below is deliberate — it is how we confirm, from a tenant's own
 * browser, that events are firing and carrying the right company_id. It has no
 * place in a renter-facing production build though, so it compiles out there:
 * CRA sets NODE_ENV=production for `yarn build` regardless of REACT_APP_ENV,
 * which makes this a constant the minifier can fold away.
 */
const debug = process.env.NODE_ENV !== "production";
const debugLog = (...args: any[]): void => {
  if (debug) console.log(...args);
};
const debugWarn = (...args: any[]): void => {
  if (debug) console.warn(...args);
};

export function initPostHog(): void {
  if (initialized || typeof window === "undefined") return;
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    autocapture: true,
    capture_pageview: false, // we fire $pageview manually on route change
    capture_pageleave: true,
    // 1NOW-10869: `?id=` on booking / agreement links is the booking's
    // unique_identifier, a credential. posthog-js copies the full url into
    // every event ($current_url, $session_entry_url, the initial-url person
    // props), so safeUrl below only ever covered the manual $pageview. Mask
    // the value at the source instead. This also masks ad click-id values
    // (gclid, fbclid, ...); their keys stay and UTM tags are untouched.
    mask_personal_data_properties: true,
    custom_personal_data_properties: ["id"],
    loaded: () => {
      debugLog("[PostHog] initialized →", POSTHOG_HOST);
    },
  });
  // Exposed for quick console testing, e.g. simulating a traffic source:
  //   window.posthog.capture('$pageview', { $referring_domain: 'www.tiktok.com' })
  (window as any).posthog = posthog;
  initialized = true;
}

/**
 * Stamp the tenant's company_id onto every subsequent event as a PostHog
 * super-property. The admin monthly-review dashboard filters all HogQL by
 * `properties.company_id`, so this is what makes the data show "according to
 * company id". Mirrors posthog.register({ company_id }) in One-Now-Frontend's
 * use-posthog.ts on the posthog-analytics branch.
 */
export function setPostHogCompany(
  companyId: number | string | null | undefined
): void {
  if (
    !initialized ||
    companyId === null ||
    companyId === undefined ||
    companyId === ""
  ) {
    debugWarn("[PostHog] setPostHogCompany skipped", {
      initialized,
      companyId,
    });
    return;
  }
  posthog.register({ company_id: String(companyId) });
  debugLog(
    "[PostHog] company_id registered as super-property →",
    String(companyId)
  );
}

/**
 * Stamp every event with whether the visit came from a paid ad (1NOW-11115), so ad and organic
 * traffic can be split in PostHog. Registered always, as a boolean, so organic visits read
 * `false` rather than missing. Call after `markLandingSource()` has run.
 */
export function setPostHogLandedFromAd(): void {
  if (!initialized) return;
  posthog.register({ landed_from_ad: landedFromAd() });
}

export function trackEvent(
  eventName: string,
  properties?: Record<string, any>
): void {
  // Mirrored to the tenant's Meta pixel where one is configured. Deliberately
  // ahead of the guard below: the two are configured independently, so a site
  // with a pixel and no PostHog key must still report to Meta.
  trackMetaEvent(eventName, properties);

  if (!initialized) {
    debugWarn("[PostHog] trackEvent skipped (not initialized):", eventName);
    return;
  }
  posthog.capture(eventName, properties);
  debugLog("[PostHog] event →", eventName, properties ?? {});
}

/**
 * Query parameters whose *values* must not leave the browser.
 *
 * `?id=` on a booking url is the booking's `unique_identifier`, and that uuid
 * is a credential: DesignCRouter and lib/funnelOrder both say so in as many
 * words, /booking-details resolves a booking from it with no sign-in, and
 * /rental-agreement serves the signed contract on it deliberately openly. So
 * a pageview sent the uuid verbatim to PostHog on every booking-page and
 * agreement view — which puts a live credential in a third party's logs, in
 * exports, and in front of anyone with dashboard access.
 *
 * The key is kept and the value replaced, because the shape of the url is
 * what a pageview is for: "/booking-details?id=redacted" groups exactly as
 * usefully as the real thing. Only this one name is redacted — `selectedCarId`
 * and `company` identify a vehicle and a tenant, not a session, and they are
 * worth having.
 */
const REDACT = new Set(["id"]);

const safeUrl = (href: string): string => {
  try {
    const u = new URL(href);
    let touched = false;
    REDACT.forEach((key) => {
      if (u.searchParams.has(key)) {
        u.searchParams.set(key, "redacted");
        touched = true;
      }
    });
    return touched ? u.toString() : href;
  } catch {
    /* Not a parseable url. Sending it unchanged is what happened before, and
       an unparseable href is not one of ours. */
    return href;
  }
};

export function capturePageview(): void {
  if (!initialized) return;
  const url = safeUrl(window.location.href);
  posthog.capture("$pageview", { $current_url: url });
  debugLog("[PostHog] $pageview →", url);
}

/**
 * 1NOW-11501: tie this browser's events to the renter's 1Now customer id once it is known, so their
 * person profile shows the whole path. The id only, never the email or phone. Prefixed because this
 * PostHog project is shared with the admin app, which identifies its users by their numeric id:
 * a bare "9" would merge a renter with an operator.
 */
export function identifyRenter(customerId: number | string | null | undefined): void {
  if (!initialized || customerId === null || customerId === undefined || customerId === "") return;
  posthog.identify(`customer_${customerId}`);
  debugLog("[PostHog] identified renter customer_" + customerId);
}

export function resetPostHog(): void {
  if (!initialized) return;
  posthog.reset();
  debugLog("[PostHog] reset");
}

/**
 * 1NOW-11121: one delegated listener so no contact component has to know about
 * analytics. Sends the kind of link only, never the number or address.
 */
const CONTACT_LINK = 'a[href^="tel:"], a[href^="mailto:"], a[href*="wa.me/"]';
let contactListening = false;
export function listenForContactClicks(): void {
  if (contactListening || typeof document === "undefined") return;
  contactListening = true;
  document.addEventListener("click", (e) => {
    const target = e.target as Element | null;
    const link = target?.closest?.(CONTACT_LINK);
    if (!link) return;
    const href = link.getAttribute("href") || "";
    trackEvent("contact_clicked", {
      contact_type: href.startsWith("tel:")
        ? "phone"
        : href.startsWith("mailto:")
        ? "email"
        : "whatsapp",
    });
  });
}

// Init as a module-load side effect (mirrors fleet's posthog-provider.tsx).
if (typeof window !== "undefined") {
  initPostHog();
  listenForContactClicks();
  captureLandingFbclid();
}

export { posthog };
